import { useContext, useMemo } from "react";
import tinycolor from "tinycolor2";
import {
    Box,
    Typography,
    FormGroup,
    MenuItem,
    Select,
    FormControl,
} from "@mui/material";
import { useSelector } from "react-redux";
import { AppContext } from "../../App";
import { selectChartConfig } from "../../selectors/chart";
import { LineSeriesOptions } from "../chartV2/chart.typings";
import ColorSelector from "../colorSelector/colorSelector";
import { clampLineThickness, LineThickness } from "../colorSelector/lineStyle.types";
import CommonChartSettingsTabs from "./commonChartSettingsTabs";
import { chartSettingsColorRowLabelSx, chartSettingsColorRowSx, chartSettingsFormGroupSx, chartSettingsRowSx, chartSettingsSectionTitleSx } from "./chartSettingsLayout";

type Props = {
    viewKey: string;
};

const LineSettingsDialogs = ({ viewKey }: Props) => {
    const appContext = useContext(AppContext)
    const chart = appContext.getChart(viewKey)
    const chartConfig = useSelector(selectChartConfig(viewKey))
    const primarySeriesGroupId = useMemo(() => {
        return chart?.primarySeriesGroup?.config?.id
    }, [chart?.primarySeriesGroup?.config?.id])
    
    const priceSettings = useMemo(() => {
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            return (series as any)?.source ?? "close"
        }
        return "close"
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])
    
    const linecolor = useMemo(() => {
        let color = '#2196f3'
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.color ?? '#2196f3'
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const lineThickness = useMemo(() => {
        let width: number | undefined
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? '']
            width = (series?.options as any)?.lineWidth ?? series?.thickness
        }
        return clampLineThickness(width)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const handleSourceChange = (newSource: 'high' | 'close' | 'open' | 'low') => {
        if (chart) {
            chart.primarySeriesGroup?.getPrimarySeries()?.updateOptions({
                source: newSource
            } as LineSeriesOptions)
        }
    };

    const handleColorChangeSeries = (newColor: string) => {
        if (chart) {
            chart.primarySeriesGroup?.getPrimarySeries()?.updateOptions({
                color: newColor
            } as LineSeriesOptions)
        }
    }

    const handleThicknessChange = (thickness: LineThickness) => {
        if (chart) {
            chart.primarySeriesGroup?.getPrimarySeries()?.updateOptions({
                lineWidth: thickness
            } as LineSeriesOptions)
        }
    }

    const symbolTabContent = (
        <Box>
            <Typography variant="subtitle1" sx={chartSettingsSectionTitleSx}>
                LINE
            </Typography>
            <FormGroup sx={chartSettingsFormGroupSx}>
                <Box sx={chartSettingsRowSx}>
                    <Typography sx={{ width: 100, fontSize: "14px", flexShrink: 0 }}>Price Source</Typography>
                    <FormControl size="small">
                        <Select
                            value={priceSettings}
                            onChange={(event) => handleSourceChange(event.target.value as 'open' | 'high' | 'low' | 'close')}
                            sx={{ fontSize: "13px", height: 32, minWidth: 100 }}
                        >
                            <MenuItem value="open">Open</MenuItem>
                            <MenuItem value="high">High</MenuItem>
                            <MenuItem value="low">Low</MenuItem>
                            <MenuItem value="close">Close</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
                <Box sx={chartSettingsColorRowSx}>
                    <Typography sx={chartSettingsColorRowLabelSx}>Line</Typography>
                    <ColorSelector
                        compact
                        showOnlySwatch
                        color={linecolor.toHexString()}
                        opacity={linecolor.getAlpha()}
                        lineThickness={lineThickness}
                        onLineThicknessChange={handleThicknessChange}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor);
                        }}
                    />
                </Box>
            </FormGroup>
        </Box>
    );

    return (
        <CommonChartSettingsTabs
            viewKey={viewKey}
            symbolTabContent={symbolTabContent}
        />
    );
};

export default LineSettingsDialogs;
